import { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { Box } from '@mui/material'

import { INTENSITY_MEDIUM_MIN } from '../../helpers/intensityThresholds'
import { displayedScoreGap } from '../../helpers/scoreDisplay'
import { useUAInsightsActions } from '../../hooks/useUAInsightsNavigation'
import { useUATaxonomy } from '../../hooks/useUATaxonomy'
import { MotivationScore } from '../../types'
import { ComparisonCard, ComparisonCategory, UnavailableComparisonDiagnosisCard } from '../ComparisonDiagnosisCard/ComparisonDiagnosisCard'
import { ComparisonDiagnosisColumn } from '../ComparisonDiagnosisColumn/ComparisonDiagnosisColumn'
import { ComparisonDiagnosisLegend } from '../ComparisonDiagnosisLegend/ComparisonDiagnosisLegend'
import { PortfolioDiagnosisCard } from '../MotivationDiagnosis/PortfolioCard'

export interface ComparisonGame {
  name: string
  /** App icon URL; shown at the source side's gap bar goalpost marker. `undefined` for genres. */
  icon?: string
  /** Game-design (Game & Live Events) motivation scores. `null` when unavailable. */
  scores: MotivationScore[] | null
}

interface ComparisonDiagnosisProps {
  source: ComparisonGame
  target: ComparisonGame
  /** Disclaimer body shown when either game has no `gameDesign` profile. */
  unavailableMessage: string
  /**
   * Hides the top portfolio-summary "DIAGNOSIS" card, leaving just the gap
   * legend and diverging cards. Used by the Compare Overview preview, which
   * already carries its own narrative summary above this section.
   */
  hideSummary?: boolean
  /**
   * Hides the inline gap legend. Used when the legend is hoisted elsewhere (e.g.
   * into the enclosing section card header) so it isn't rendered twice.
   */
  hideLegend?: boolean
}

/**
 * Cross-game classifier for the `gameDesign` (Game & Live Events) signal:
 *  - `aligned`   → both games rate the motivation at or above the medium
 *                  threshold (i.e., it is a core driver in both games).
 *  - `blind_spot` → exactly one game rates the motivation as core; the other
 *                  is below the threshold.
 *  - `null`      → both games are below the threshold; the motivation isn't
 *                  a meaningful angle for either game and is dropped from
 *                  the diagnosis (matches how single-game diagnosis discards
 *                  noise rows).
 *
 * The threshold is the same `INTENSITY_MEDIUM_MIN` used by the single-game
 * diagnosis so the two diagnostics stay calibrated against the same notion
 * of "registers as more than background noise".
 */
function classifyPair(sourceScore: number, targetScore: number): ComparisonCategory | null {
  const sHigh = sourceScore >= INTENSITY_MEDIUM_MIN
  const tHigh = targetScore >= INTENSITY_MEDIUM_MIN
  if (sHigh && tHigh) return 'aligned'
  if (sHigh !== tHigh) return 'blind_spot'
  return null
}

/**
 * Divergence magnitude between two sides, computed from the displayed (1-decimal)
 * scores so it always reconciles with the values shown on the card (e.g. 2.0 /
 * 1.9 → 0.1, never a raw 0.2). Used for both the sort order and the insight
 * tier so the chip, the ordering, and the sentence all agree.
 */
function roundedGap(sourceScore: number, targetScore: number): number {
  return Math.abs(displayedScoreGap(sourceScore, targetScore))
}

/**
 * Returns the i18n key for a diverging card's explanation, varied by how far
 * the two sides split so cards in the column read with different stories
 * rather than one boilerplate line. Mirrors the single-game diagnosis
 * blind-spot tiers (`severe` / `clear` / `mild`) so the two surfaces stay
 * calibrated against the same notion of "how big is this gap".
 */
function comparisonInsightKey(gap: number): string {
  if (gap >= 2) return 'compare_diverging_severe'
  if (gap >= 1.2) return 'compare_diverging_clear'
  return 'compare_diverging_mild'
}

/**
 * Builds the ordered list of "diverging" (blind-spot) motivation pairs between
 * two games — motivations exactly one game rates as a core driver — sorted by
 * the widest gap first. Shared by {@link ComparisonDiagnosis} and the Compare
 * Overview so the header legend and the column agree on whether any cards exist.
 * Returns an empty array when either game lacks an in-game profile.
 */
export function getComparisonBlindSpots(source: ComparisonGame, target: ComparisonGame): ComparisonCard[] {
  if (!source.scores || !target.scores) return []

  const blindSpots: ComparisonCard[] = []

  // Walk the source order — both arrays follow the canonical `MOTIVATIONS`
  // sequence emitted by `projectScores`, so a positional zip is enough; no
  // name lookup needed.
  for (let i = 0; i < source.scores.length; i += 1) {
    const sourceScore = source.scores[i]
    const targetScore = target.scores[i]
    if (!targetScore) continue
    const category = classifyPair(sourceScore.avgIntensity, targetScore.avgIntensity)
    if (category !== 'blind_spot') continue
    blindSpots.push({
      motivation: sourceScore.motivation,
      category,
      scores: { source: sourceScore.avgIntensity, target: targetScore.avgIntensity },
      insightKey: comparisonInsightKey(roundedGap(sourceScore.avgIntensity, targetScore.avgIntensity)),
    })
  }

  // Largest gap first so the motivations the games disagree on most prominently
  // surface at the top of the column.
  blindSpots.sort((a, b) => roundedGap(b.scores.source, b.scores.target) - roundedGap(a.scores.source, a.scores.target))

  return blindSpots
}

/**
 * "Diverging motivations" diagnosis comparing the source and target games on
 * their `gameDesign` motivation profile only — per the product notes, the
 * comparison should not invent extra data when either game is missing the
 * in-game profile.
 *
 * Visually mirrors the single-game `MotivationDiagnosis` blind-spots column
 * (error-tinted) so users moving between the single-game page and the
 * comparison dialog see a consistent diagnostic vocabulary. The column + cards
 * live in {@link ComparisonDiagnosisColumn} / {@link ComparisonDiagnosisCard};
 * this file is intentionally a thin orchestrator that classifies + sorts
 * the per-motivation pairs and opens the drilldown.
 */
export const ComparisonDiagnosis: FC<ComparisonDiagnosisProps> = ({ source, target, unavailableMessage, hideSummary, hideLegend }) => {
  const { t } = useTranslation('ua-insights')
  const { openDivergingMotivation } = useUAInsightsActions()
  const { motivationLabel } = useUATaxonomy()

  if (!source.scores || !target.scores) {
    return <UnavailableComparisonDiagnosisCard body={unavailableMessage} />
  }

  const blindSpots = getComparisonBlindSpots(source, target)

  // Lead-in summary mirroring the single-game diagnosis: name-check the widest
  // divergence (the first card after sorting) and count the rest, or call out
  // that the two sides move in step when nothing diverges.
  const summaryText =
    blindSpots.length === 0
      ? t('compare_diagnosis_summary_none', { source: source.name, target: target.name })
      : t('compare_diagnosis_summary', {
          count: blindSpots.length,
          source: source.name,
          target: target.name,
          worst: motivationLabel(blindSpots[0].motivation),
        })

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {!hideSummary && <PortfolioDiagnosisCard text={summaryText} subtitle={t('compare_diagnosis_subtitle', { source: source.name, target: target.name })} />}
      {/* Section-level legend mirroring the single-game blind-spots diagnosis:
          the gap bar's goalpost marker is the source, the solid fill is the
          target, and the hatched band is the gap between them. Only shown when
          there are cards to read against (the empty state speaks for itself),
          and skipped when hoisted into the section card header. */}
      {!hideLegend && blindSpots.length > 0 && <ComparisonDiagnosisLegend source={source} target={target} />}
      <ComparisonDiagnosisColumn
        category="blind_spot"
        cards={blindSpots}
        emptyText={t('compare_blind_spots_empty')}
        sourceName={source.name}
        targetName={target.name}
        sourceIcon={source.icon}
        targetIcon={target.icon}
        onCardClick={openDivergingMotivation}
      />
    </Box>
  )
}
